import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialVideo.css';
import ReactPlayer from 'react-player';

const SocialVideo = ({ post_id, url, poster, seeingVideo, autoSound = true }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Si autoSound est true, le son sera activé automatiquement
  const [isMuted, setIsMuted] = useState(!autoSound);

  // Préparer les URLs
  const videoUrl = url ? (url.startsWith('http') 
    ? url 
    : `https://peupleqc.com/s/peupleqc-upload/${url}`) : '';
  
  const posterUrl = poster ? (poster.startsWith('http') 
    ? poster 
    : `https://peupleqc.com/s/peupleqc-upload/${poster}`) : '';

  // Hydration sécurisée - exécution uniquement côté client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Configuration de l'observer de visibilité
  useEffect(() => {
    if (!mounted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        console.log("Video visibility changed:", entry.isIntersecting);
        setIsVisible(entry.isIntersecting);
        
        if (entry.isIntersecting && typeof seeingVideo === "function" && post_id) {
          seeingVideo(post_id);
        }
      },
      { threshold: 0.3 }  // Considéré visible quand 30% de l'élément est visible (plus sensible)
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Vérification initiale de la visibilité
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const isInitiallyVisible = 
        rect.top < window.innerHeight &&
        rect.bottom > 0;
      
      if (isInitiallyVisible) {
        console.log("Video initially visible, auto-playing");
        setIsVisible(true);
      }
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [mounted, post_id, seeingVideo]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handlePlayerReady = () => {
    setIsLoaded(true);
  };

  // Si non monté (côté serveur), afficher un espace réservé
  if (!mounted) {
    return <div className="social-video-container" ref={containerRef}></div>;
  }

  return (
    <div 
      className={`social-video-container ${isVisible ? 'is-visible' : ''}`} 
      ref={containerRef}
      onClick={toggleMute}
      data-testid={`video-container-${post_id}`}
    >
      {/* Bouton pour activer/désactiver le son */}
      {isMuted ? (
        <div className="social-video-mute-button">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
          </svg>
        </div>
      ) : (
        <div className="social-video-mute-button">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        </div>
      )}
      
      <div className="social-video-player-wrapper">
        <ReactPlayer
          ref={playerRef}
          key={`player-${post_id}-${isVisible}`} // Force re-render when visibility changes
          url={videoUrl}
          width="100%"
          height="100%"
          playing={isVisible} // Lance automatiquement quand visible
          muted={isMuted}
          controls={false} // Sans contrôles pour ressembler à TikTok
          loop
          playsinline
          onReady={handlePlayerReady}
          onPlay={() => console.log("Video started playing, id:", post_id)}
          onPause={() => console.log("Video paused, id:", post_id)}
          onError={(e) => console.error("Video error:", e)}
          config={{
            file: {
              attributes: {
                poster: posterUrl,
                controlsList: 'nodownload',
                preload: 'auto',
                autoPlay: isVisible
              },
              forceVideo: true,
              hlsOptions: {
                autoStartLoad: isVisible
              }
            }
          }}
        />
      </div>
      
      {!isLoaded && (
        <div className="social-video-loading">
          <div className="social-video-spinner"></div>
        </div>
      )}
      
      {posterUrl && !isLoaded && (
        <div className="social-video-poster">
          <img 
            src={posterUrl}
            alt="Video thumbnail"
            className="social-video-poster-image"
          />
        </div>
      )}
    </div>
  );
};

export { SocialVideo };